// src/pages/HomePage.tsx
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import ScrollReveal from '../components/ScrollReveal'
import styles from './HomePage.module.css'

const SERVICES = [
  {
    icon: '🏋️',
    title: 'Gym & Fitness Vending',
    desc: 'Protein shakes, pre-workout, electrolytes, and recovery products. Keep your members fuelled on the floor — zero extra staff.',
    tag: 'Gyms · Studios · Courts',
    anchor: '/services#gyms',
  },
  {
    icon: '🍺',
    title: 'Bar & Nightlife Vending',
    desc: 'Vape products, accessories, and late-night convenience items. Add a silent revenue stream to your venue — no extra work.',
    tag: 'Bars · Clubs · Lounges',
    anchor: '/services#bars',
  },
  {
    icon: '🍬',
    title: 'Snacks & Convenience',
    desc: 'Classic snacks, drinks, and grab-and-go favourites for offices, hotels, salons, and any high-traffic Edmonton location.',
    tag: 'Offices · Hotels · Retail',
    anchor: '/services#snacks',
  },
]

const STEPS = [
  { num: '01', icon: '📞', title: 'Free Consultation', desc: "Tell us about your space, foot traffic, and customer base. We'll recommend the perfect machine and product mix." },
  { num: '02', icon: '🗺️', title: 'Site Assessment', desc: 'We visit your venue, confirm the best spot, handle all logistics, and finalize the product selection.' },
  { num: '03', icon: '⚡', title: 'Installation', desc: 'We deliver, place, and set up the machine at zero cost. Typically done in under two hours with minimal disruption.' },
  { num: '04', icon: '📈', title: 'Ongoing Revenue', desc: 'We restock and maintain everything. You earn a passive revenue share from every sale — hands-free.' },
]

const TESTIMONIALS = [
  { initials: 'MK', name: 'Mike K.', biz: 'Gym Owner, Edmonton, AB', text: '"FreshDispense installed a protein vending machine at our gym and our members love it. Zero hassle and we get a monthly cheque. Can\'t ask for more."' },
  { initials: 'SR', name: 'Sarah R.', biz: 'Bar Manager, Edmonton, AB', text: '"The machine they put in our bar has been a hit. FreshDispense handled everything — we literally did nothing except say yes."' },
  { initials: 'JL', name: 'Jennifer L.', biz: 'Office Manager, YEG', text: '"Professional, fast, and the machine has been stocked perfectly from day one. Our staff loves the snack options."' },
]

const FAQS = [
  { q: 'How much does it cost to get a vending machine at my Edmonton location?', a: 'Nothing. FreshDispense provides completely free vending machine placement across Edmonton and Alberta. We invest in the machine, installation, and stock. Qualified host locations earn a revenue share from every sale.' },
  { q: 'What types of vending machines do you place in Edmonton?', a: 'We specialize in protein and supplement vending for Edmonton gyms, vape and accessory vending for bars and lounges, snack and beverage vending for offices and hotels, and fully custom solutions for any Edmonton venue type.' },
  { q: 'Who handles restocking and maintenance?', a: 'FreshDispense handles everything — restocking, cleaning, technical maintenance, and repairs. Your only job as a host is to let us access the machine.' },
  { q: 'What areas in Edmonton does FreshDispense serve?', a: 'We serve all of Edmonton and the greater YEG area, including surrounding communities in Alberta.' },
  { q: 'How long does installation take?', a: 'Installation is typically completed in under two hours and causes minimal disruption to your business. We schedule at a time that works for you.' },
]

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
}

function Counter({ target, suffix = '', prefix = '' }: { target: number, suffix?: string, prefix?: string }) {
  const [val, setVal] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const started = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true
        const duration = 1800
        const start = performance.now()
        const step = (now: number) => {
          const progress = Math.min((now - start) / duration, 1)
          const ease = 1 - Math.pow(1 - progress, 3)
          setVal(Math.round(ease * target))
          if (progress < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
        observer.unobserve(el)
      }
    }, { threshold: 0.5 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [target])

  return <span ref={ref}>{prefix}{val}{suffix}</span>
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={styles.faqItem}>
      <button
        className={styles.faqQ}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <span className={`${styles.faqIcon} ${open ? styles.faqIconOpen : ''}`}>+</span>
      </button>
      <div className={`${styles.faqA} ${open ? styles.faqAOpen : ''}`}>
        <p>{a}</p>
      </div>
    </div>
  )
}

export default function HomePage() {
  const marqueeRef = useRef<HTMLDivElement>(null)

  const handleScrollPrompt = () => {
    const nextSection = document.querySelector('section[aria-label="Hero"] + *') as HTMLElement | null
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      window.scrollBy({ top: 240, behavior: 'smooth' })
    }
  }

  return (
    <>
      <SEOHead
        title="FreshDispense | Vending Machine Placement Edmonton, Alberta | YEG Vending Solutions"
        description="Edmonton's #1 vending machine placement company. FreshDispense installs custom vending machines in gyms, bars, offices & more across YEG. Free placement — we handle everything."
        canonical="/"
        keywords="vending machine Edmonton, vending machine placement Edmonton, Edmonton vending company, YEG vending solutions, gym vending Edmonton, bar vending Edmonton, office vending Edmonton"
        schema={FAQ_SCHEMA}
      />

      <main className={`page-enter ${styles.main}`}>
        {/* ─── HERO ─── */}
        <section className={styles.hero} aria-label="Hero">
          <div className={styles.heroBg} aria-hidden />
          <div className={styles.heroGrid} aria-hidden />
          <div className={styles.heroOrb} aria-hidden />
          <div className="container">
            <div className={styles.heroContent}>
              <div className={`badge badge-blue ${styles.heroEyebrow} reveal`}>
                <span className="badge-dot" />
                Edmonton, AB · YEG's Vending Specialists
              </div>
              <h1 className={`${styles.heroH1} reveal`} style={{ animationDelay: '0.2s' }}>
                The Right Vending<br />
                Machine. <em className={styles.heroEm}>The Right</em><br />
                Place.
              </h1>
              <p className={`${styles.heroSub} reveal`} style={{ animationDelay: '0.2s' }}>
                FreshDispense partners with Edmonton gyms, bars, offices, and venues to install
                smart, revenue-generating vending machines like Benvend — custom-matched to your customers.
                We handle everything.
              </p>
              <div className={`${styles.heroActions} reveal`} style={{ animationDelay: '0.3s' }}>
                <Link to="/contact" className="btn btn-primary">Get a Free Placement →</Link>
                <Link to="/services" className="btn btn-outline">See Our Services</Link>
              </div>
              <div className={`${styles.heroProof} reveal`} style={{ animationDelay: '0.4s' }}>
                {['Edmonton-based company', 'Benvend & gym vending', 'Full-service stocking'].map(t => (
                  <div key={t} className={styles.proofItem}>
                    <span className={styles.proofDot} />
                    {t}
                  </div>
                ))}
              </div>
              <div className={`${styles.heroVisual} reveal`} style={{ animationDelay: '0.5s' }}>
                <div className={styles.heroCard}>
                  <img src="/HERO.png" alt="FreshDispense vending hero" className={styles.heroCardImg} />
                  <div className={styles.heroCardCTA}>
                    <span className="badge badge-green">Featured</span>
                    <h3>Launch your first vending placement today.</h3>
                    <p>Book a free site review and see how vending can work in your Edmonton location.</p>
                    <div className={styles.heroCardActions}>
                      <Link to="/contact" className="btn btn-primary btn-hero">Start a Free Placement</Link>
                      <Link to="/contact" className="btn btn-outline btn-hero">Book a Free Site Visit</Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.heroScrollPrompt} reveal`} style={{ animationDelay: '0.55s' }}>
            <button type="button" className={styles.scrollButton} onClick={handleScrollPrompt} aria-label="Scroll down">
              <span className={styles.scrollArrow}>↓</span>
            </button>
            <span>Scroll down</span>
          </div>
        </section>

        {/* ─── MARQUEE ─── */}
        <div className={styles.marqueeWrap} aria-hidden="true">
          <div className={styles.marqueeInner} ref={marqueeRef}>
            {[...Array(2)].flatMap((_, ri) =>
              ['🏋️ Gym Vending Edmonton', '🍺 Bar Vending Edmonton', '🏢 Office Vending YEG',
               '🍬 Snack Vending Alberta', '💪 Protein Vending Edmonton', '🛠 Free Installation YEG',
               '📍 Edmonton Vending Company'].map((item, i) => (
                <span key={`${ri}-${i}`} className={styles.marqueeItem}>{item}<span className={styles.marqueeSep}>✦</span></span>
              ))
            )}
          </div>
        </div>

        {/* ─── STATS ─── */}
        <section className="section-sm" aria-label="Stats">
          <div className="container">
            <ScrollReveal>
              <div className="stats-row">
                <div className="stat-block">
                  <div className="stat-num gradient-text"><Counter target={100} suffix="%" /></div>
                  <div className="stat-label">Free Placement — No Upfront Cost</div>
                </div>
                <div className="stat-block">
                  <div className="stat-num gradient-text">YEG</div>
                  <div className="stat-label">Born & Based in Edmonton, Alberta</div>
                </div>
                <div className="stat-block">
                  <div className="stat-num gradient-text">24/7</div>
                  <div className="stat-label">Revenue for Your Location</div>
                </div>
                <div className="stat-block">
                  <div className="stat-num gradient-text"><Counter target={4} suffix="+" /></div>
                  <div className="stat-label">Specialized Vending Categories</div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── SERVICES ─── */}
        <section className="section" id="services" aria-labelledby="services-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">What We Offer</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="services-heading" style={{ fontSize: 'clamp(30px,4.5vw,56px)', marginBottom: '0.5rem' }}>
                Custom Vending for Every<br />Edmonton Venue
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p style={{ maxWidth: 560, fontSize: 17, marginBottom: '3rem' }}>
                We don't place random machines — we match the perfect product to the perfect location
                so your customers get real value, every visit.
              </p>
            </ScrollReveal>

            <div className={styles.serviceStrip}>
              {SERVICES.map((s, i) => (
                <ScrollReveal key={s.title} delay={i * 100}>
                  <article className={styles.serviceCard}>
                    <div className={styles.serviceCardTop}>
                      <div className={styles.serviceIcon}>{s.icon}</div>
                      <h3>{s.title}</h3>
                      <p>{s.desc}</p>
                    </div>
                    <div className={styles.serviceCardBottom}>
                      <span className={styles.serviceTag}>{s.tag}</span>
                      <Link to={s.anchor} className={styles.serviceLink}>Learn more →</Link>
                    </div>
                  </article>
                </ScrollReveal>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <ScrollReveal>
                <Link to="/services" className="btn btn-outline">View All Services</Link>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className={styles.howSection} aria-labelledby="how-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">The Process</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="how-heading" style={{ fontSize: 'clamp(30px,4.5vw,56px)', marginBottom: '0.75rem' }}>
                How FreshDispense Works
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p style={{ maxWidth: 520 }}>
                Four simple steps from first call to a fully running machine at your Edmonton location.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className={styles.stepsGrid}>
                {STEPS.map((s) => (
                  <div key={s.num} className={styles.stepCard}>
                    <div className={styles.stepNum}>{s.num}</div>
                    <div className={styles.stepIcon}>{s.icon}</div>
                    <h3 style={{ fontSize: 18, marginBottom: '0.75rem', color: 'var(--text)' }}>{s.title}</h3>
                    <p style={{ fontSize: 14 }}>{s.desc}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── VIDEO ─── */}
        <section className={`${styles.videoSection} section`} aria-label="Company video">
          <div className="container">
            <div className={styles.videoLayout}>
              <div>
                <ScrollReveal><span className="label-tag">See It In Action</span></ScrollReveal>
                <ScrollReveal delay={100}>
                  <h2 style={{ fontSize: 'clamp(28px,3.5vw,48px)', margin: '0.5rem 0 1rem' }}>
                    Watch FreshDispense<br />At Work
                  </h2>
                </ScrollReveal>
                <ScrollReveal delay={200}>
                  <p style={{ fontSize: 17, marginBottom: '2rem' }}>
                    From gym floor to bar counter — see how our vending machines deliver real value
                    in Edmonton's best venues.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={300}>
                  <Link to="/contact" className="btn btn-primary">Book a Placement →</Link>
                </ScrollReveal>
              </div>
              <ScrollReveal delay={200}>
                <div className={styles.videoBox}>
                  <span className={styles.videoTag}>📹 Benvend Demo</span>
                  <video
                    className={styles.videoPlayer}
                    controls
                    poster="/gymvend.png"
                    preload="metadata"
                  >
                    <source src="/VedioOfGym.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ─── TESTIMONIALS ─── */}
        <section className={styles.testiSection} aria-labelledby="reviews-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">What Partners Say</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="reviews-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '3rem' }}>
                Edmonton Businesses Love FreshDispense
              </h2>
            </ScrollReveal>
            <div className="grid-3">
              {TESTIMONIALS.map((t, i) => (
                <ScrollReveal key={t.name} delay={i * 100}>
                  <div className={styles.testiCard}>
                    <div className={styles.testiStars}>★★★★★</div>
                    <p className={styles.testiText}>{t.text}</p>
                    <div className={styles.testiAuthor}>
                      <div className={styles.testiAvatar}>{t.initials}</div>
                      <div>
                        <div className={styles.authorName}>{t.name}</div>
                        <div className={styles.authorBiz}>{t.biz}</div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className={styles.faqSection} aria-labelledby="faq-heading">
          <div className="container" style={{ maxWidth: 820 }}>
            <ScrollReveal><span className="label-tag">Common Questions</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="faq-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '2.5rem' }}>
                FAQ About Vending Machines<br />in Edmonton
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className={styles.faqList}>
                {FAQS.map(f => <FAQItem key={f.q} q={f.q} a={f.a} />)}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="section" aria-label="Call to action">
          <div className="container">
            <ScrollReveal>
              <div className="cta-banner">
                <span className="badge badge-green" style={{ marginBottom: '1.5rem' }}>
                  <span className="badge-dot" />Free Placement Available
                </span>
                <h2 style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '1rem' }}>
                  Ready to Add a Vending Machine<br />to Your Edmonton Business?
                </h2>
                <p style={{ fontSize: 18, maxWidth: 520, margin: '0 auto 2.5rem' }}>
                  Book a free, no-obligation consultation. We'll find the perfect vending solution
                  for your space — at zero cost to you.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/contact" className="btn btn-primary">Get My Free Placement →</Link>
                  <a href="tel:+17801234567" className="btn btn-outline">Call Us in Edmonton</a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  )
}
